import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ShieldAlert, ShieldCheck, FileCode2, LayoutDashboard, BookOpen, Sparkles, Bug, Award } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ConsultCtaBand from "../components/ConsultCtaBand";
import { QelliPublicCTA } from "../components/qelli/QelliPublicCTA";
import QelliPublicNewsRadar from "../components/qelli/QelliPublicNewsRadar";
import ChainMarquee from "../components/ChainMarquee";
import PublicRiskIndex from "../components/PublicRiskIndex";
import CustomerRadarShowcase from "../components/landing/CustomerRadarShowcase";
import LeaderboardSection from "../components/landing/LeaderboardSection";
import TestimonialWall from "../components/landing/TestimonialWall";
import ProductBentoGrid from "../components/landing/ProductBentoGrid";
import LandingChoosePath from "../components/landing/LandingChoosePath";
import ProcessTimeline from "../components/landing/ProcessTimeline";
import UseCaseTabs from "../components/landing/UseCaseTabs";
import TerminalFooterBlock from "../components/landing/TerminalFooterBlock";
import SectionPunctuator from "../components/landing/SectionPunctuator";
import ScrollProgress from "../components/landing/ScrollProgress";
import DecryptedRotor from "../components/docs/DecryptedRotor";
import KeywordSweep from "../components/KeywordSweep";
import ExposureSyncText from "../components/ExposureSyncText";
import AnimatedExposureCard from "../components/AnimatedExposureCard";
import useExperiment from "../lib/useExperiment";
import useDocumentTitle from "../hooks/useDocumentTitle";
import usePageMeta from "../hooks/usePageMeta";

const THREAT_INTEL = [
  {
    headline: "$400B+",
    detail:   "in BTC sits in addresses with revealed public keys — fully exposed to a quantum adversary today.",
    accent:   "#FF2A2A",
  },
  {
    headline: "100%",
    detail:   "of Solana addresses are quantum-exposed by design. The address IS the public key. No hash protection.",
    accent:   "#FFB000",
  },
  {
    headline: "ECDSA · EdDSA · Schnorr · BLS",
    detail:   "every signature scheme used across the top 18 chains is broken by Shor's algorithm under a CRQC.",
    accent:   "#00E5FF",
  },
];

export default function Landing() {
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0] === "en";
  useDocumentTitle("QorTrace — Post-Quantum Risk Intelligence for the OnChain Economy");
  usePageMeta({
    title: "QorTrace — Post-Quantum Risk Intelligence for the OnChain Economy",
    description: "Free PQC wallet scan, full BOM audits, live trust posture, and Q-Day threat intelligence — across 18 chains. QorTrace is post-quantum risk intelligence for the onchain economy.",
    canonicalPath: "/",
  });

  // Iter214 — Inject Schema.org JSON-LD (Organization + SoftwareApplication
  // + AggregateRating + Reviews) into <head> so Google + most modern
  // crawlers index our testimonials as structured-review data.
  useEffect(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    if (!base) return;
    let cancelled = false;
    const tag = document.createElement("script");
    tag.type  = "application/ld+json";
    tag.id    = "qortrace-jsonld";
    document.head.appendChild(tag);
    fetch(`${base}/api/public/qortrace-jsonld`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (cancelled || !d) return;
        tag.textContent = JSON.stringify(d);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      const el = document.getElementById("qortrace-jsonld");
      if (el && el.parentNode) el.parentNode.removeChild(el);
    };
  }, []);

  const heroExp = useExperiment("landing-hero", {
    tier:    "default",
    source:  "landing-hero",
    fallback: {
      label:    "NOW LIVE · 18 CHAINS · REAL-TIME DATA & ANALYSIS",
      headline: "",   // headline kept brand-static; variant only swaps pill + CTA
      cta:      "Scan a wallet — free",
    },
  });
  return (
    <div className="min-h-screen qt-radial-bg">
      {/* Iter240 — tiny cyan scroll-progress bar pinned to top of viewport. */}
      <ScrollProgress />
      <Header />

      {/* HERO */}
      <section className="relative qt-grain">
        <div className="absolute inset-0 qt-hero-grid pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 pt-16 sm:pt-24 pb-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end relative">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="qt-pulse" style={{ width: 8, height: 8, borderRadius: 99, background: "#00E5FF" }} />
              <span className="qt-label">METHODOLOGY v0.1 · LIVE</span>
            </div>
            <Link to="/free-scan" data-testid="hero-launch-pill"
                  data-variant={heroExp.variant}
                  onClick={heroExp.recordClick}
                  className="inline-flex items-center gap-2 mb-5 qt-mono text-xs px-3 py-1.5 rounded-full"
                  style={{ background: "rgba(0,229,255,0.06)", color: "var(--qt-cyan)", border: "1px solid var(--qt-cyan)", letterSpacing: "0.05em" }}>
              <span className="qt-pulse" style={{ width: 6, height: 6, borderRadius: 99, background: "var(--qt-cyan)" }} />
              {(isEnglish && heroExp.copy.label) || t("landing.hero_pill", "NOW LIVE · 18 CHAINS · REAL-TIME DATA & ANALYSIS")} <ArrowRight size={12} />
            </Link>
            <h1 className="qt-display text-[36px] sm:text-[50px] xl:text-[62px]" data-testid="hero-title">
              <ExposureSyncText
                testId="hero-title-sync"
                style={{ display: "inline" }}
                keywords={[
                  "Audits",
                  "Security",
                  "Risk Intelligence",
                  "PQC Migration",
                  "OnChain Economy",
                ]}
                text={
                  "Quantum-Ready Audits, " +
                  "Security, Risk Intelligence " +
                  "and PQC Migration for the " +
                  "Global OnChain Economy"
                }
              />
            </h1>
            <DecryptedRotor
              taglines={[
                "Quantum-safe audits.",
                "Risk intelligence.",
                "PQC migration.",
                "NIST-aligned methodology.",
                "Migration guidance.",
                "Software development.",
                "Quantum security services & products.",
                "Deep-dive audits & analysis.",
                "Wallet scanning.",
                "Treasury monitoring.",
              ]}
              className="mt-5 text-base sm:text-lg"
              testId="hero-rotor"
            />
            <KeywordSweep
              testId="hero-subtitle"
              className="mt-6 text-lg max-w-2xl"
              style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}
              accent="#00E5FF"
              perKeywordMs={1100}
              keywords={[
                "cryptographic risk", "intelligence", "defense",
                "wallet scans", "smart-contract audits",
                "treasury monitoring", "PQC migration",
                "blockchains", "DeFi", "exchanges", "custodians",
                "banks", "governments", "institutions",
              ]}
              text={
                "The cryptographic risk, intelligence and defense platform " +
                "powering wallet scans, smart-contract audits, signed PQC " +
                "certificates, treasury monitoring, and PQC migration for " +
                "blockchains, DeFi, exchanges, custodians, banks, " +
                "governments and the institutions holding the keys."
              }
            />
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/scan" data-testid="hero-cta-scan"
                    data-variant={heroExp.variant}
                    onClick={heroExp.recordClick}
                    className="qt-btn qt-btn-primary">
                {(isEnglish && heroExp.copy.cta) || t("landing.cta_scan", "Scan a wallet — free")} <ArrowRight size={16} />
              </Link>
              <Link to="/methodology" data-testid="hero-cta-methodology" className="qt-btn qt-btn-secondary">
                {t("landing.cta_methodology", "See methodology")}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-4">
            <AnimatedExposureCard />
          </div>
        </div>

        {/* Full-width chain marquee — eyebrow label sits with equal vertical
            buffer above the logos and the next CTA card so the row visually
            centers between them. */}
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 relative">
          <div className="qt-mono text-xs sm:text-sm mb-16"
               data-testid="hero-marquee-label"
               style={{ color: "var(--qt-cyan)", letterSpacing: "0.12em", fontWeight: 700 }}>
            {t("landing.marquee_label", "↓ CLICK ANY SUPPORTED CHAIN BELOW FOR A QUICK SCAN")}
          </div>
          <ChainMarquee size={44} gap={32} source="hero-marquee" />
        </div>
      </section>

      {/* FREE-SCAN CTA — prominent funnel */}
      <section className="max-w-[1400px] mx-auto px-6 sm:px-8 pt-16 pb-16">
        <div className="qt-card qt-grain"
             style={{ background: "#000000",
                      border: "1px solid var(--qt-cyan)" }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-8">
              <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>{t("landing.free_scan_eyebrow", "FREE PREVIEW · NO CARD · 30 SECONDS")}</div>
              <h2 className="qt-h2 text-3xl sm:text-5xl mt-2">
                {t("landing.free_scan_title", "Run a free post-quantum scan on any wallet you own.")}
              </h2>
              <p className="mt-3 text-base max-w-2xl" style={{ color: "var(--qt-text-2)" }}>
                {t("landing.free_scan_body", "Create a free account, paste an address from any of 18 chains, and get a deterministic exposure score with basic findings emailed to you. Watermark-free deep-dive reports live on the paid plans.")}
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <Link to="/free-scan" data-testid="landing-free-scan-cta" className="qt-btn qt-btn-primary"
                    style={{ justifyContent: "center", padding: "0.85rem 1.25rem" }}>
                {t("landing.free_scan_cta", "Run free scan")} <ArrowRight size={16} />
              </Link>
              <Link to="/pricing" className="qt-btn qt-btn-secondary"
                    style={{ justifyContent: "center", padding: "0.85rem 1.25rem" }}>
                {t("landing.free_scan_plans", "See plans")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* THREAT INTEL · 3-card */}
      <section className="max-w-[1400px] mx-auto px-6 sm:px-8 py-16">
        <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>{t("landing.threat_eyebrow", "WHY THIS MATTERS · THREAT INTELLIGENCE")}</div>
        <h2 className="qt-h2 text-3xl sm:text-5xl mt-2 max-w-3xl">
          {t("landing.threat_title", "The cryptographic clock is already ticking.")}
        </h2>
        <p className="mt-3 max-w-3xl" style={{ color: "var(--qt-text-2)" }}>
          {t("landing.threat_body", "NIST finalized FIPS 203/204/205 in August 2024. Harvest-now-decrypt-later is documented adversary behavior. Every chain we cover relies on signatures Shor's algorithm breaks.")}
        </p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="threat-intel-grid">
          {THREAT_INTEL.map((t, i) => (
            <ThreatCard key={i} idx={i} {...t} />
          ))}
        </div>
      </section>

      {/* PUBLIC RISK INDEX · live aggregate across every scan */}
      <PublicRiskIndex />
      <SectionPunctuator />

      {/* Iter238 — Qelli hero promoted to ~25% scroll depth.
          Visitors meet the AI guide BEFORE they wade through the
          deeper product/methodology sections below. Reads like
          "and by the way, you don't have to figure this out alone". */}
      <QelliPublicCTA variant="hero" />
      <QelliPublicNewsRadar />
      <SectionPunctuator />

      {/* Iter204 — Customer-verified threat-radar showcase. Renders
          nothing until at least one customer has verified a domain. */}
      <CustomerRadarShowcase />
      <SectionPunctuator />

      {/* Iter208 — Top 10 leaderboard. Ranks customer radars by
          (shares × 3 + views × 1) over the last 7 days. */}
      <LeaderboardSection />
      <SectionPunctuator />

      {/* Iter212 — "What customers are saying" wall. Renders nothing
          until at least one consented testimonial exists. */}
      <TestimonialWall />
      <SectionPunctuator />

      {/* SMART-CONTRACT AUDIT BAND · drive visitors to the free preview.
          Placed AFTER the risk index so users who came in for PQC scanning
          still see the quant-threat stats first, then get a second-product
          nudge: "you're here for scanning, but if you write contracts,
          try the audit engine free". */}
      <AuditBand />

      {/* PRODUCT BENTO */}
      <section className="max-w-[1400px] mx-auto px-6 sm:px-8 py-20">
        <div className="qt-label mb-3" data-testid="product-bento-eyebrow">THE QORTRACE STACK</div>
        <h2 className="qt-h2 text-3xl sm:text-5xl max-w-3xl" data-testid="product-bento-headline">
          Scan. Analyze. Certify. Monitor.
        </h2>
        <p className="mt-4 max-w-2xl" style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
          Four surfaces, one cryptographic risk model. Check a single wallet, audit
          a Solidity contract, ship a signed certificate buyers actually trust, and
          monitor an entire treasury — every chain, every signature scheme, every
          quantum-relevant primitive. The same NIST-aligned methodology powers all four.
        </p>

        <div className="mt-10">
          <ProductBentoGrid />
        </div>
      </section>

      {/* HOW IT WORKS — Iter237: vertical timeline w/ asymmetric reveal */}
      <section className="max-w-[1100px] mx-auto px-6 sm:px-8 py-16">
        <div className="qt-label mb-3">HOW IT WORKS</div>
        <h2 className="qt-h2 text-3xl sm:text-5xl max-w-3xl">
          Three steps, one auditable trail.
        </h2>
        <ProcessTimeline />
      </section>

      {/* USE CASES — Iter237: tabbed switcher, no longer 3 identical cards */}
      <section className="max-w-[1400px] mx-auto px-6 sm:px-8 py-20">
        <div className="qt-label mb-3">USE CASES</div>
        <h2 className="qt-h2 text-3xl sm:text-5xl max-w-3xl">
          Built for who custodies the risk.
        </h2>
        <p
          className="mt-3 max-w-2xl text-sm"
          style={{ color: "var(--qt-text-2)", lineHeight: 1.65 }}
        >
          Same engine, three lenses. Pick the persona that matches how
          you hold cryptographic risk.
        </p>
        <div className="mt-10">
          <UseCaseTabs />
        </div>
      </section>

      {/* METHODOLOGY + TRUST — Iter237: terminal vs certificate split */}
      <section className="max-w-[1400px] mx-auto px-6 sm:px-8 py-12">
        <div className="qt-label mb-3">OPEN · AUDITABLE · LIVE</div>
        <h2 className="qt-h2 text-3xl sm:text-5xl max-w-3xl">
          Read the recipe. Verify the posture.
        </h2>
        <p
          className="mt-3 max-w-2xl text-sm"
          style={{ color: "var(--qt-text-2)", lineHeight: 1.65 }}
        >
          The QorTrace methodology is versioned and open. Our compliance
          posture is live and continuously attested.
        </p>
        <div className="mt-8">
          <TerminalFooterBlock />
        </div>
      </section>

      <ConsultCtaBand source="landing-bottom-cta" variant="card" maxWidth={1400} />
      <Footer />
    </div>
  );
}

function ThreatCard({ headline, detail, accent, idx }) {
  return (
    <div className="qt-card qt-threat-card"
         data-testid={`threat-card-${idx}`}
         style={{
           background: "#000000",
           border: `1px solid ${accent}66`,
           padding: 28,
           boxShadow: `0 10px 36px -12px ${accent}40, 0 0 60px -20px ${accent}33, inset 0 0 80px -40px ${accent}22`,
           animation: `qt-fade-up 0.5s ease ${idx * 0.12}s both`,
         }}>
      <div className="qt-mono text-[10px]" style={{ color: accent, letterSpacing: "0.18em", fontWeight: 700 }}>
        ◆ THREAT VECTOR · 0{idx + 1}
      </div>
      <div className="qt-display mt-4"
           style={{ color: accent, fontSize: "clamp(28px, 4vw, 42px)", lineHeight: 1.05, letterSpacing: "-0.02em", textShadow: `0 0 24px ${accent}33` }}>
        {headline}
      </div>
      <div className="mt-3 text-base" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
        {detail}
      </div>
      <style>{`
        @keyframes qt-fade-up {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .qt-threat-card { transition: transform .25s ease, border-color .25s ease, box-shadow .25s ease; }
        .qt-threat-card:hover {
          transform: translateY(-3px);
          border-color: ${accent};
          box-shadow: 0 18px 48px -10px ${accent}66, 0 0 80px -16px ${accent}55, inset 0 0 80px -40px ${accent}33;
        }
      `}</style>
    </div>
  );
}



function ProductTile({ to, testid, step, verb, tag, icon, title, text, cta }) {
  return (
    <Link to={to} data-testid={testid} className="qt-card qt-card-interactive flex flex-col group">
      {(step || verb) && (
        <div
          className="inline-flex items-center self-start gap-2 qt-mono text-[10px] mb-3 px-2 py-1 rounded-full"
          data-testid={`${testid}-step`}
          style={{
            background: "rgba(0,229,255,0.08)",
            color: "var(--qt-cyan)",
            border: "1px solid rgba(0,229,255,0.35)",
            letterSpacing: "0.18em",
            fontWeight: 700,
          }}
        >
          {step && <span style={{ opacity: 0.6 }}>{step}</span>}
          {step && verb && <span style={{ opacity: 0.4 }}>·</span>}
          {verb && <span>{verb}</span>}
        </div>
      )}
      <div className="flex items-start justify-between">
        <div className="qt-label">{tag}</div>
        <div style={{ color: "var(--qt-cyan)" }}>{icon}</div>
      </div>
      <div className="qt-h2 text-2xl mt-4">{title}</div>
      <p className="mt-3 text-sm" style={{ color: "var(--qt-text-2)" }}>{text}</p>
      <div className="mt-6 flex items-center gap-2 qt-mono text-xs"
           style={{ color: "var(--qt-cyan)", letterSpacing: "0.15em" }}>
        {cta.toUpperCase()} <ArrowRight size={14} />
      </div>
    </Link>
  );
}

// Full-width band selling the audit engine's free-preview. Rendered between
// the PQC risk-index (PublicRiskIndex) and the product bento so visitors who
// came in for wallet scanning get a second, product-specific hook:
// "if you write contracts, try the engine live — no signup".
function AuditBand() {
  return (
    <section className="max-w-[1400px] mx-auto px-6 sm:px-8 py-12" data-testid="landing-audit-band">
      <div
        className="qt-card qt-grain relative overflow-hidden"
        style={{
          border: "1px solid rgba(0,229,255,0.35)",
          background:
            "linear-gradient(135deg, rgba(0,229,255,0.08) 0%, rgba(0,229,255,0.02) 40%, transparent 80%)",
          padding: "2rem 2.25rem",
        }}
      >
        {/* subtle chain-grid accent — mirrors the hero */}
        <div className="absolute inset-0 qt-hero-grid pointer-events-none opacity-40" />

        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div
              className="inline-flex items-center gap-2 mb-4 qt-mono text-xs px-3 py-1.5 rounded-full"
              style={{
                background: "rgba(0,229,255,0.08)",
                color: "var(--qt-cyan)",
                border: "1px solid var(--qt-cyan)",
                letterSpacing: "0.06em",
              }}
              data-testid="audit-band-pill"
            >
              <Sparkles size={12} /> NEW · SMART CONTRACT AUDITS · 18 CHAINS
            </div>
            <h2
              className="qt-display text-[30px] sm:text-[40px] xl:text-[48px]"
              style={{ lineHeight: 1.08, letterSpacing: "-0.015em" }}
            >
              Audit <span style={{ color: "#00E5FF" }}>your contract</span>{" "}
              in 30 seconds. Free.
            </h2>
            <p
              className="mt-4 max-w-2xl text-base"
              style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}
            >
              Paste Solidity, Move, Rust, Cairo or Vyper — the same engine
              that powers our paid audits returns <strong style={{ color: "#fff" }}>severity-tiered findings, code excerpts, and remediation</strong> in under a minute.
              No signup required. Save to your account for a public
              certificate you can embed anywhere.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/audit/preview"
                data-testid="audit-band-cta-preview"
                className="qt-btn qt-btn-primary"
                style={{ fontSize: 14, padding: "0.75rem 1.25rem" }}
              >
                <Sparkles size={14} /> Try a free preview <ArrowRight size={14} />
              </Link>
              <Link
                to="/audit"
                data-testid="audit-band-cta-learn"
                className="qt-btn qt-btn-ghost"
                style={{
                  fontSize: 14,
                  padding: "0.75rem 1.25rem",
                  border: "1px solid var(--qt-border)",
                }}
              >
                See how it works
              </Link>
            </div>
          </div>

          {/* Mini-bullets — keeps the band self-contained, avoids making the
              user click to understand the value prop. */}
          <div className="lg:col-span-4 grid grid-cols-1 gap-3">
            <BandBullet
              icon={<Bug size={14} />}
              label="Real findings"
              body="Reentrancy, access-control, integer safety, oracle manipulation."
            />
            <BandBullet
              icon={<ShieldCheck size={14} />}
              label="Scored 0–100"
              body="Security + Trust scores, identical to our paid audit path."
            />
            <BandBullet
              icon={<Award size={14} />}
              label="Shareable certificate"
              body="Save your preview → get SVG / PNG / HTML for README & docs."
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function BandBullet({ icon, label, body }) {
  return (
    <div
      className="flex items-start gap-3 px-3 py-2.5 rounded"
      style={{
        background: "rgba(4, 9, 20, 0.4)",
        border: "1px solid var(--qt-border)",
      }}
    >
      <div
        style={{ color: "var(--qt-cyan)", flexShrink: 0, marginTop: 2 }}
      >
        {icon}
      </div>
      <div>
        <div
          className="qt-mono text-[10px]"
          style={{ color: "var(--qt-cyan)", letterSpacing: "0.18em", fontWeight: 700 }}
        >
          {label.toUpperCase()}
        </div>
        <div className="text-xs mt-1" style={{ color: "var(--qt-text-2)", lineHeight: 1.5 }}>
          {body}
        </div>
      </div>
    </div>
  );
}

function UseCaseCard({ role, copy }) {
  return (
    <div className="qt-card">
      <div className="qt-label">{role.toUpperCase()}</div>
      <div className="mt-4 text-lg" style={{ color: "#fff", lineHeight: 1.5 }}>{copy}</div>
    </div>
  );
}
